import useSWR from "swr";
import { Box, Button, Flex, Select, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { CloseCircle } from "iconsax-react";
import baseService from "../../services/base";
import { COLORS } from "../../theme/dashboardTokens";

// EG-38 (filtros BI) — barra de filtros consistente do portfólio/operação.
// value = { exercise, certificateId, groupId }; onChange recebe o novo objeto.
// Inclui "Limpar filtros" (passo 4: reset consistente).
const fetcher = (url) => baseService.get(url).then((r) => r?.data);

function Chip({ active, children, onClick }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      px="12px"
      py="6px"
      borderRadius="full"
      fontSize="13px"
      fontWeight={active ? 600 : 500}
      bg={active ? "#E7F1FA" : "#F5F5FB"}
      color={active ? COLORS.brand : COLORS.inkMuted}
      border={`1px solid ${active ? COLORS.brand : "#E2E8F0"}`}
    >
      {children}
    </Box>
  );
}

export default function FilterBar({ value, onChange, show = ["exercise", "certificate", "group"] }) {
  const { data: options } = useSWR("metrics/filters", fetcher);
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const hasAny =
    v.exercise != null || v.certificateId != null || v.groupId != null || v.periodDays != null;

  const exercises = options?.exercises ?? [];
  const certificates = options?.certificates ?? [];
  const groups = options?.groups ?? [];

  return (
    <Flex bg={COLORS.surface} borderRadius="12px" p="14px 18px" mb="24px" gap="16px" align="center" wrap="wrap">
      {/* Sem rótulo ao lado, como os outros dois selects desta barra: o
          "Exercício" escrito à esquerda deixava este campo mais largo e
          desalinhado do resto da linha. O placeholder já diz o que é. */}
      {show.includes("exercise") && exercises.length > 0 && (
        <Select
          size="sm"
          maxW="180px"
          borderRadius="8px"
          placeholder="Todos os exercícios"
          value={v.exercise ?? ""}
          onChange={(e) => set({ exercise: e.target.value ? Number(e.target.value) : null })}
        >
          {exercises.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      )}

      {show.includes("certificate") && (
        <Select
          size="sm"
          maxW="240px"
          borderRadius="8px"
          placeholder="Todos os certificados"
          value={v.certificateId ?? ""}
          onChange={(e) => set({ certificateId: e.target.value ? Number(e.target.value) : null })}
        >
          {certificates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.exercise ? `${c.name} (${c.exercise})` : c.name}
            </option>
          ))}
        </Select>
      )}

      {show.includes("group") && (
        <Select
          size="sm"
          maxW="240px"
          borderRadius="8px"
          placeholder="Todos os grupos"
          value={v.groupId ?? ""}
          onChange={(e) => set({ groupId: e.target.value ? Number(e.target.value) : null })}
        >
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.description}
            </option>
          ))}
        </Select>
      )}

      {show.includes("period") && (
        <Flex align="center" gap="8px" wrap="wrap">
          <Text fontSize="13px" color={COLORS.inkMuted}>
            Período
          </Text>
          <Wrap spacing="6px">
            {[
              { label: "Tudo", days: null },
              { label: "90 dias", days: 90 },
              { label: "6 meses", days: 180 },
              { label: "12 meses", days: 365 },
            ].map((p) => (
              <WrapItem key={p.label}>
                <Chip
                  active={(v.periodDays ?? null) === p.days}
                  onClick={() => set({ periodDays: p.days })}
                >
                  {p.label}
                </Chip>
              </WrapItem>
            ))}
          </Wrap>
        </Flex>
      )}

      {hasAny && (
        <Button
          size="sm"
          variant="ghost"
          color={COLORS.inkMuted}
          leftIcon={<CloseCircle size="16" color={COLORS.inkMuted} />}
          onClick={() => onChange({})}
        >
          Limpar filtros
        </Button>
      )}
    </Flex>
  );
}

// Monta a query string dos filtros (ex.: "?exercise=2026&groupId=4").
export function filtersToQuery(v = {}) {
  const parts = [];
  if (v.exercise != null) parts.push(`exercise=${v.exercise}`);
  if (v.certificateId != null) parts.push(`certificateId=${v.certificateId}`);
  if (v.groupId != null) parts.push(`groupId=${v.groupId}`);
  if (v.periodDays != null) parts.push(`periodDays=${v.periodDays}`);
  return parts.length ? `?${parts.join("&")}` : "";
}
